import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cn, debounce } from "@/lib/utils";
import { faInfoCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Popover, PopoverTrigger, PopoverContent } from "./popover";
import { Button } from "./button";
import { Calendar } from "./calendar";
import { CalendarIcon  } from "lucide-react";
import { format ,parse,isValid} from "date-fns";
import { useMaskito } from '@maskito/react'
import {maskitoDateOptionsGenerator} from '@maskito/kit';

export interface InputProps extends React.HTMLAttributes<HTMLDivElement> {
  asChild?: boolean;
  id: string;
  placeholder: string;
  prefix?: string;
  title?: string;
  titleClassName?: string;
  divClassName?: string;
  onChange: React.FormEventHandler;
  showError?: boolean;
  showFormatError?: boolean;
  hideErrorMessage?: boolean;
  type?: string;
  value?: any;
}

const Input = React.forwardRef<HTMLDivElement, InputProps>(
  (
    {
      className,
      asChild = false,
      id,
      placeholder,
      prefix,
      title,
      titleClassName,
      divClassName,
      onChange,
      children,
      showError,
      showFormatError,
      hideErrorMessage,
      type,
      value,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : "div";
    const [selectedDate, setSelectedDate] = React.useState<Date | undefined>(
      value ? new Date(value) : undefined
    );
    //https://github.com/shadcn-ui/ui/issues/448
    const maskedInputRef = useMaskito({ options: maskitoDateOptionsGenerator({mode: 'yyyy/mm/dd', separator: '-'}) })

    
    const handleDateChange = (date: Date | undefined) => {
      if (date && onChange) {
        setSelectedDate(date);

        const event = {
          target: {
            id,
            value: format(date, 'yyyy-MM-dd'),
          },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(event); // Simulate an input change event
      }
    };

    return (
      <Comp className={divClassName} ref={ref} {...props}>
        {title == null ? (
          <></>
        ) : (
          <p
            className={cn(
              `mb-2 ${showError ? "text-red-500" : ""}`,
              titleClassName
            )}
          >
            {title}
          </p>
        )}
        <div
          className={cn(
            `border-[1px] ${showError ? "border-red-500" : "border-gray-300"} rounded-md p-2 flex`,
            className
          )}
        >
          {prefix == null ? <></> : <img src={prefix} />}
          {type === "date" ? 
          ( <Popover>
            <PopoverTrigger asChild>
              <Button
                variant={"ghost"}
                className={cn(
                  "w-full justify-start text-left font-normal -m-1",
                  !selectedDate && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="-ml-3 mr-2" />
                {selectedDate && value
                  ? `${selectedDate && isValid(selectedDate) ? format(selectedDate, 'yyyy-MM-dd') : ''}`
                  : <span className="pr-2">Pick a date</span>}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0">
            <input
            id={id}
            value={selectedDate && isValid(selectedDate) ? format(selectedDate, 'yyyy-MM-dd') : ''}
            className="m-2 w-[95%] outline-0 bg-transparent border-[1px] rounded-md p-2 -pr-2 "
            placeholder={"YYYY-MM-DD"}
            onBlur={debounce((e) => {
              const parsedDate = parse(e.target.value, "yyyy-MM-dd", new Date());
              if (isValid(parsedDate)) {
                console.log("onBlur",e.target.value, parsedDate);
                handleDateChange(parsedDate)
     
              }
            }, 300)}
            // Wrap your existing keyUp logic in debounce:
            onKeyUp={debounce((e) => {
              console.log("onKeyUp",e.target.value);

              const parsedDate = parse(e.target.value, "yyyy-MM-dd", new Date());
              if (isValid(parsedDate)) {

                handleDateChange(parsedDate)

              }
            }, 700)}
            onChangeCapture={debounce((e) => {
              const parsedDate = parse(e.target.value, "yyyy-MM-dd", new Date());
              if (isValid(parsedDate)) {
                console.log("onChange",e.currentTarget.value, parsedDate);
                handleDateChange(parsedDate)

              }
            }, 300)}
            ref={maskedInputRef}
            type={"text"}
          />
              <Calendar
                mode="single"
                selected={selectedDate}
                onSelect={handleDateChange}
                month={selectedDate}
                initialFocus
              />
            </PopoverContent>
          </Popover>) : (<input
            id={id}
            value={value}
            className="ml-2 w-full outline-0 bg-transparent"
            placeholder={placeholder}
            onChange={onChange}
            type={type ?? "text"}
          />)}
        </div>
        {!hideErrorMessage && (
          <div
            className={`text-xs ${showError ? "text-red-500" : "hidden"} font-medium flex flex-row items-center space-x-1 mt-1`}
          >
            <FontAwesomeIcon icon={faInfoCircle} />
            <span>{title?.replace("*", "")} {showFormatError ? "format is wrong" : "cannot be empty"}</span>
          </div>
        )}
      </Comp>
    );
  }
);

Input.displayName = "Input";

export { Input };
